import { Alert as ChakraAlert } from '@chakra-ui/react';
import { forwardRef } from 'react';

export interface AlertProps extends Omit<ChakraAlert.RootProps, 'title'> {
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  title?: React.ReactNode;
  icon?: React.ReactElement;
}

/**
 * A closed composition of Alert.
 *
 * @see https://chakra-ui.com/docs/components/alert
 * @see https://chakra-ui.com/docs/components/alert#closed-component
 *
 * @example
 * ```tsx
 * <Alert status="info" title="Alert with only title" />
 *
 * <Alert
 *   status="warning"
 *   title="Alert with title and Description"
 *   icon={<LuCloudLightning />}
 * >
 *   <div>
 *     <p>multi-part description</p>
 *   </div>
 * </Alert>
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    const { title, children, icon, startElement, endElement, ...rest } = props;
    return (
      <ChakraAlert.Root ref={ref} {...rest}>
        {startElement || <ChakraAlert.Indicator>{icon}</ChakraAlert.Indicator>}
        {children ? (
          <ChakraAlert.Content>
            <ChakraAlert.Title>{title}</ChakraAlert.Title>
            <ChakraAlert.Description>{children}</ChakraAlert.Description>
          </ChakraAlert.Content>
        ) : (
          <ChakraAlert.Title flex="1">{title}</ChakraAlert.Title>
        )}
        {endElement}
      </ChakraAlert.Root>
    );
  },
);
